import React, { useState } from "react";
import {
  Box, Typography, Paper, Button, TextField, Stack,
  IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  useGetFaqs, useCreateFaq, useUpdateFaq, useToggleFaq, useDeleteFaq,
} from "./faqQueries";

// ── Edit/Create Dialog ───────────────────────────────────────────────────────
const FaqDialog = ({ open, onClose, initial }) => {
  const isEdit = Boolean(initial);
  const { mutate: create, isLoading: creating } = useCreateFaq();
  const { mutate: update, isLoading: updating } = useUpdateFaq();

  const [form, setForm] = useState(
    initial || { question: "", answer: "" }
  );

  React.useEffect(() => {
    setForm(initial || { question: "", answer: "" });
  }, [initial, open]);

  const handleSave = () => {
    if (!form.question.trim() || !form.answer.trim()) return;
    if (isEdit) {
      update({ id: initial.id, question: form.question, answer: form.answer }, { onSuccess: onClose });
    } else {
      create({ question: form.question, answer: form.answer }, { onSuccess: onClose });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Question"
            fullWidth
            multiline
            rows={2}
            value={form.question}
            onChange={(e) => setForm((p) => ({ ...p, question: e.target.value }))}
          />
          <TextField
            label="Answer"
            fullWidth
            multiline
            rows={4}
            value={form.answer}
            onChange={(e) => setForm((p) => ({ ...p, answer: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={creating || updating || !form.question.trim() || !form.answer.trim()}
        >
          {creating || updating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────
const DeleteDialog = ({ open, onClose, faq }) => {
  const { mutate: del, isLoading } = useDeleteFaq();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete FAQ?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Are you sure you want to delete: <strong>{faq?.question}</strong>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={isLoading}
          onClick={() => del(faq.id, { onSuccess: onClose })}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main FAQ Management Page ──────────────────────────────────────────────────
const FAQ = () => {
  const { data, isLoading } = useGetFaqs();
  const { mutate: toggle } = useToggleFaq();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const faqs = data?.data || [];

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (faq) => { setEditing(faq); setDialogOpen(true); };
  const closeDialog = () => setDialogOpen(false);

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", py: 4, px: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700} color="primary">
          Manage FAQs
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add FAQ
        </Button>
      </Stack>

      {isLoading && <Typography>Loading...</Typography>}

      {!isLoading && faqs.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography color="text.secondary">No FAQs yet. Click "Add FAQ" to create one.</Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {faqs.map((faq) => (
          <Paper
            key={faq.id}
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              opacity: faq.isActive ? 1 : 0.6,
              borderColor: faq.isActive ? "grey.200" : "warning.light",
              bgcolor: faq.isActive ? "white" : "grey.50",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Typography fontWeight={700} variant="body1">
                    {faq.question}
                  </Typography>
                  {!faq.isActive && (
                    <Chip label="Hidden" size="small" color="warning" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                  {faq.answer}
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Tooltip title={faq.isActive ? "Hide" : "Show"}>
                  <IconButton
                    size="small"
                    onClick={() => toggle(faq.id)}
                    color={faq.isActive ? "warning" : "success"}
                  >
                    {faq.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(faq)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setDeleteTarget(faq)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <FaqDialog open={dialogOpen} onClose={closeDialog} initial={editing} />
      <DeleteDialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} faq={deleteTarget} />
    </Box>
  );
};

export default FAQ;
